import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Copy, Trash2, FileDown, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AiComposer } from "@/components/quote/AiComposer";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { useApi, errMsg } from "@/lib/api";
import { eur, itDate } from "@/lib/pricing";
import { STATUS } from "@/lib/constants";
import { openPdf } from "@/lib/pdf";

const StatusBadge = ({ s }) => (
  <span className={`text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${STATUS[s]?.cls}`}>{STATUS[s]?.label || s}</span>
);

const QuoteRow = ({ q, onDup, onDel, onPdf }) => (
  <tr className="border-b border-slate-800/80 hover:bg-slate-800/30 transition-colors" data-testid={`quote-row-${q.id}`}>
    <td className="py-3 px-3 font-mono text-amber-400 whitespace-nowrap">
      <Link to={`/preventivi/${q.id}`} data-testid={`quote-open-${q.id}`}>{q.number ? q.code : <span className="text-slate-400">Bozza</span>}{q.number && q.status === "bozza" ? <span className="ml-1 text-[10px] text-slate-400">bozza</span> : null}</Link>
    </td>
    <td className="py-3 px-3">
      <Link to={`/preventivi/${q.id}`} className="block hover:text-amber-300">
        <div className="font-medium">{q.client?.name || <span className="text-slate-500">Senza cliente</span>}</div>
        <div className="text-xs text-slate-400 line-clamp-1">{q.category && <span className="font-mono uppercase text-[10px] px-1.5 mr-1.5 rounded bg-amber-400/10 text-amber-300" data-testid={`quote-category-badge-${q.id}`}>{q.category}</span>}{q.subject || "—"}</div>
      </Link>
    </td>
    <td className="py-3 px-3 text-sm text-slate-300 whitespace-nowrap hidden md:table-cell">{[itDate(q.event_date), q.event_date_end && q.event_date_end !== q.event_date ? itDate(q.event_date_end) : ""].filter(Boolean).join(" – ") || "—"}</td>
    <td className="py-3 px-3 text-sm text-slate-400 hidden lg:table-cell">{q.days} gg · {q.line_count} voci</td>
    <td className="py-3 px-3 text-right font-mono whitespace-nowrap" data-testid={`quote-total-${q.id}`}>{eur(q.totals.totale)}</td>
    <td className="py-3 px-3 hidden sm:table-cell"><StatusBadge s={q.status} /></td>
    <td className="py-3 px-3">
      <div className="flex justify-end gap-1">
        <Button size="icon" variant="ghost" onClick={() => onPdf(q)} title="Scarica PDF" data-testid={`quote-pdf-${q.id}`}><FileDown className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => onDup(q)} title="Duplica" data-testid={`quote-dup-${q.id}`}><Copy className="h-4 w-4" /></Button>
        <ConfirmDelete onConfirm={() => onDel(q)} title={`Eliminare il preventivo ${q.code}?`} testId={`quote-del-${q.id}`}>
          <Trash2 className="h-4 w-4" />
        </ConfirmDelete>
      </div>
    </td>
  </tr>
);

export default function QuotesPage() {
  const api = useApi();
  const nav = useNavigate();
  const [quotes, setQuotes] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("bozza");

  const [all, setAll] = useState([]);
  const load = useCallback(() => {
    api.get("/quotes", { params: { q: search, status } })
      .then((r) => setQuotes(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/quotes").then((r) => setAll(r.data)).catch(() => {});
  }, [api, search, status]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const onDup = async (q) => {
    try {
      const r = await api.post(`/quotes/${q.id}/duplicate`);
      toast.success(`Creato ${r.data.code}`);
      nav(`/preventivi/${r.data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onDel = async (q) => {
    try { await api.delete(`/quotes/${q.id}`); toast.success("Preventivo eliminato"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const onPdf = (q) => openPdf(api, q, true).catch((e) => toast.error(errMsg(e, "PDF non disponibile")));
  const total = all.filter((q) => q.status === "accettato").reduce((s, q) => s + q.totals.totale, 0);

  return (
    <div className="space-y-8 rise" data-testid="quotes-page">
      <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
        <div>
          <p className="micro-label">Dashboard</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Preventivi</h1>
          <p className="text-slate-400 mt-2 text-sm sm:text-base max-w-2xl">Descrivi l'evento a parole o a voce: l'AI sceglie le voci dal tuo listino e apre il preventivo pronto da rifinire.</p>
          <div className="mt-5">
            <AiComposer onText={async (text) => nav("/preventivi/nuovo", { state: { aiText: text } })} />
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
          <Stat label="Bozze / Inviati" value={`${all.filter((q) => q.status === "bozza").length} / ${all.filter((q) => q.status !== "bozza").length}`} testId="stat-count" />
          <Stat label="Accettati (totale)" value={eur(total)} testId="stat-accepted" />
        </div>
      </div>

      <div className="bg-slate-900/60 border border-slate-800 rounded-lg">
        <div className="flex flex-col sm:flex-row gap-3 p-4 border-b border-slate-800">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca per cliente, oggetto o luogo" className="pl-9" data-testid="quotes-search-input" />
          </div>
          <div className="flex rounded-md border border-slate-700 p-0.5 bg-slate-950/60" data-testid="quotes-tabs">
            {[["bozza", "Bozze"], ["inviati", "Inviati"], ["", "Tutti"]].map(([k, l]) => (
              <button key={k || "all"} type="button" onClick={() => setStatus(k)} data-testid={`quotes-tab-${k || "all"}`}
                className={`px-4 h-9 rounded text-sm transition-colors ${status === k ? "bg-amber-400 text-slate-900 font-semibold" : "text-slate-300 hover:bg-slate-800"}`}>{l}</button>
            ))}
          </div>
        </div>
        {!quotes ? (
          <div className="p-10 grid place-items-center"><Loader2 className="h-5 w-5 animate-spin text-amber-400" /></div>
        ) : quotes.length === 0 ? (
          <div className="p-12 text-center" data-testid="quotes-empty">
            <FileText className="h-8 w-8 mx-auto text-slate-600" />
            <p className="text-slate-400 mt-3">{status === "inviati" ? "Nessun preventivo inviato." : "Nessuna bozza. Usa la composizione rapida qui sopra o crea un preventivo vuoto."}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left" data-testid="quotes-table">
              <thead>
                <tr className="micro-label border-b border-slate-800">
                  <th className="py-2 px-3 font-medium">N°</th>
                  <th className="py-2 px-3 font-medium">Cliente / Oggetto</th>
                  <th className="py-2 px-3 font-medium hidden md:table-cell">Data evento</th>
                  <th className="py-2 px-3 font-medium hidden lg:table-cell">Durata</th>
                  <th className="py-2 px-3 font-medium text-right">Totale</th>
                  <th className="py-2 px-3 font-medium hidden sm:table-cell">Stato</th>
                  <th className="py-2 px-3" />
                </tr>
              </thead>
              <tbody>{quotes.map((q) => <QuoteRow key={q.id} q={q} onDup={onDup} onDel={onDel} onPdf={onPdf} />)}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ label, value, testId }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
    <div className="micro-label">{label}</div>
    <div className="text-2xl font-display font-semibold mt-1 font-mono" data-testid={testId}>{value}</div>
  </div>
);
